import { gql } from '@apollo/client';
import IconError from '@apollo/icons/default/IconError.svg';
import IconWarning from '@apollo/icons/default/IconWarning.svg';
import * as monacoEditor from 'monaco-editor';
import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';

import { LinterRuleDescriptionMarkdownRenderer } from 'src/app/graph/checksPage/linterConfigPage/linterRuleDescriptionMarkdownRenderer/LinterRuleDescriptionMarkdownRenderer';
import { ExampleCode } from 'src/app/graph/checksPage/linterConfigPage/linterRulesCard/exampleCode/ExampleCode';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { EditorViewZone } from 'src/components/editorViewZone/EditorViewZone';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { LintDiagnosticLevel } from 'src/lib/graphqlTypes/types';

import { Violation } from '../../lintCheckList/ruleViolationList/RuleViolationList';
import {
  OverrideViolationButton,
  OverrideViolationButtonIgnoredRuleFragment,
  OverrideViolationButtonLintDiagnosticFragment,
} from '../../overrideViolationButton/OverrideViolationButton';

const VIEW_ZONE_PADDING_IN_PX = 14;
const VIEW_ZONE_VIOLATION_HEIGHT_IN_PX = 24;
const VIEW_ZONE_VIOLATION_SPACING_IN_PX = 6;

export {
  OverrideViolationButtonIgnoredRuleFragment,
  OverrideViolationButtonLintDiagnosticFragment,
};

export const EditorViolationLinterRuleLevelConfigurationFragment = gql`
  fragment EditorViolationLinterRuleLevelConfigurationFragment on LinterRuleLevelConfiguration {
    rule
    goodExampleCode
    badExampleCode
  }
`;

export const EditorViolation = ({
  editor,
  lineNumber,
  violations,
  rules,
  subgraphName,
  ignoredLinterViolations,
}: {
  editor: monacoEditor.editor.IStandaloneCodeEditor;
  lineNumber: number;
  violations: Violation[];
  rules: GraphQLTypes.EditorViolationLinterRuleLevelConfigurationFragment[];
  subgraphName: string | null;
  ignoredLinterViolations: GraphQLTypes.OverrideViolationButtonIgnoredRuleFragment[];
}) => {
  const marginDomNode = useMemo(() => document.createElement('div'), []);
  const heightInPx = useMemo(
    () =>
      VIEW_ZONE_PADDING_IN_PX +
      violations.length * VIEW_ZONE_VIOLATION_HEIGHT_IN_PX +
      (violations.length - 1) * VIEW_ZONE_VIOLATION_SPACING_IN_PX,
    [violations.length],
  );
  useEffect(() => {
    marginDomNode.className = 'z-20';
  }, [marginDomNode]);

  return (
    <>
      {createPortal(
        <div
          className="flex flex-col items-center"
          style={{ height: heightInPx }}
        >
          <div className="flex-1 border-l border-silver-darker" />
          <div className="size-2 rounded-full border border-silver-darker" />
          <div className="flex-1 border-l border-silver-darker" />
        </div>,
        marginDomNode,
      )}
      <EditorViewZone
        className="w-full"
        lineNumber={lineNumber + 1}
        editor={editor}
        viewZoneOptions={{
          heightInPx,
          marginDomNode,
        }}
      >
        <div className="w-full space-y-1.5 border-y border-primary py-1.5 shadow-sm">
          {violations.map((violation) => {
            const rule = rules.find((r) => r.rule === violation.rule);
            return (
              <div
                key={`${violation.rule}-${violation.coordinate}`}
                className="flex w-full items-center justify-between px-3"
              >
                <div className="flex max-w-5/6 items-center font-mono text-xs">
                  {violation.level === LintDiagnosticLevel.ERROR && (
                    <>
                      <IconError className="mr-2 text-icon-error" />
                      <span className="mr-1 inline-block font-semibold text-black-lighter">
                        Error:
                      </span>
                    </>
                  )}
                  {violation.level === LintDiagnosticLevel.WARNING && (
                    <>
                      <IconWarning className="mr-2 text-icon-warning" />
                      <span className="mr-1 inline-block font-semibold text-black-lighter">
                        Warning:
                      </span>
                    </>
                  )}
                  <div className="z-20 mr-2">
                    <p className="truncate text-neutral [&_code]:text-xs">
                      {(rule?.goodExampleCode || rule?.badExampleCode) && (
                        <Tooltip
                          label={
                            <ExampleCode
                              goodExampleCode={rule?.goodExampleCode ?? ''}
                              badExampleCode={rule?.badExampleCode ?? ''}
                            />
                          }
                          interactive
                          placement="end"
                          className="z-50 ml-3"
                        >
                          <span className="text-pink-dark">
                            {violation.rule}
                          </span>
                        </Tooltip>
                      )}{' '}
                    </p>
                  </div>
                  <div className="z-50 truncate">
                    <Tooltip
                      label={
                        <LinterRuleDescriptionMarkdownRenderer
                          descriptionMarkdown={violation?.message ?? ''}
                        />
                      }
                      placement="top-start"
                    >
                      <span
                      // this wrapper is necessary since Tooltip needs the ref of the element to add mouse events listeners to and LinterRuleDescriptionMarkdownRenderer/safeCompileMarkdown/markdown-to-jsx doesn't forward refs
                      >
                        <LinterRuleDescriptionMarkdownRenderer
                          descriptionMarkdown={violation?.message ?? ''}
                        />
                      </span>
                    </Tooltip>
                  </div>
                </div>
                {violation.__typename === 'LintDiagnostic' && (
                  <OverrideViolationButton
                    violation={violation}
                    subgraphName={subgraphName}
                    ignoredLinterViolations={ignoredLinterViolations}
                    defaultOverrideButtonClassName="z-50 ml-auto h-6 w-fit min-w-fit"
                    undoButtonClassName="z-50 text-xs"
                  />
                )}
              </div>
            );
          })}
        </div>
      </EditorViewZone>
    </>
  );
};
